import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useCallback, useMemo, useState } from 'react';
import { useMedia } from 'react-use';

import { MatchaAutoBanner } from '@/components/MatchaAutoBanner';
import type { TradeDirection } from '@/components/TransactionModule/types';

import { useUrlTokenPair } from '@/hooks/transactionModule/useUrlTokenPair';
import { CheckoutModes, useCheckoutMode } from '@/hooks/useCheckoutMode';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { dialogCloseBtnStyles } from '@/styles/dialog';
import { breakpoints } from '@/styles/util';

import { XIcon } from '@/ui/Icons/XIcon';
import { BottomPanelModal } from '@/ui/Modal/BottomPanelModal';
import { Panel } from '@/ui/Panel';

import type { TokenBase } from '@/utils/0x/token-registry.types';
import { isSameTokenAddress } from '@/utils/normalizeAddress';
import { isSameToken } from '@/utils/tradeRoutes';

import { HistorySection } from './HistorySection';
import { HomepageHeading } from './HomepageHeading';
import { MarketingStrip } from './MarketingStrip';
import { TokenPanel, TokenPanelSide } from './TokenPanel';
import { TradeModuleCard } from './TradeModuleCard';

// Desktop chart height that keeps the token panel level with the trade module.
const DESKTOP_CHART_HEIGHT = 334;

export const Homepage = () => {
  const { isConnected, walletsReady, restoringWalletAddresses } = useMatchaWallets();
  const walletConnected = isConnected || !!restoringWalletAddresses;
  const [checkoutMode] = useCheckoutMode();
  const [autoBannerVisible, setAutoBannerVisible] = useState(false);
  const [isTokenPanelOpen, setTokenPanelOpen] = useState(false);
  // The sheet's chart only activates once the opening animation has settled, so
  // its first render does not compete with the animation.
  const [isTokenPanelReady, setTokenPanelReady] = useState(false);
  // The chart column stays mounted below the desktop breakpoint (CSS keeps
  // the layout hydration-stable), but its polling fetches are suspended so
  // phones do not pay for an invisible chart.
  const isDesktopViewport = useMedia(breakpoints.desktop, false);
  const isTabletOrDesktop = useMedia(breakpoints.tablet, false);
  const {
    sellToken,
    buyToken,
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    handleFlipSelectedTokens,
    handleSelectToken,
    handleSelectPair,
    chartToken,
    handleSelectChartToken,
  } = useUrlTokenPair('/');

  const side: TokenPanelSide =
    sellTokenAddress &&
    sellTokenChainId &&
    isSameToken(chartToken, { address: sellTokenAddress, chainId: sellTokenChainId })
      ? 'sell'
      : 'buy';

  const handleSideChange = useCallback(
    (next: TokenPanelSide) => {
      if (next === side) return;
      const [address, chainId] =
        next === 'buy' ? [buyTokenAddress, buyTokenChainId] : [sellTokenAddress, sellTokenChainId];
      if (address && chainId) handleSelectChartToken({ address, chainId });
    },
    [
      buyTokenAddress,
      buyTokenChainId,
      handleSelectChartToken,
      sellTokenAddress,
      sellTokenChainId,
      side,
    ],
  );

  // History follows the charted token on both tabs.
  const scopeToken = side === 'buy' ? buyToken : sellToken;
  const scopeReady =
    !!scopeToken && (!sellTokenAddress || !!sellToken) && (!buyTokenAddress || !!buyToken);

  // A history row's token logo buys that token; the token already on the sell
  // side flips the pair instead of landing on both sides.
  const handleSelectBuyToken = useCallback(
    (token: Pick<TokenBase, 'address' | 'chainId'>) => {
      if (
        token.chainId === sellTokenChainId &&
        isSameTokenAddress(token.address, sellTokenAddress)
      ) {
        handleFlipSelectedTokens();
        return;
      }
      handleSelectToken(token, 'buy');
    },
    [handleFlipSelectedTokens, handleSelectToken, sellTokenAddress, sellTokenChainId],
  );
  const buySelection = useMemo(
    () => ({
      current: buyTokenAddress ? { address: buyTokenAddress, chainId: buyTokenChainId } : undefined,
      onSelect: handleSelectBuyToken,
    }),
    [buyTokenAddress, buyTokenChainId, handleSelectBuyToken],
  );

  // Below the desktop breakpoint the panel column is hidden, so the module's
  // token info buttons surface the same panel in a bottom sheet.
  const handleOpenTokenPanel = useCallback(
    (direction: TradeDirection) => {
      handleSideChange(direction);
      setTokenPanelReady(false);
      setTokenPanelOpen(true);
    },
    [handleSideChange],
  );

  const bannerChainId = sellTokenChainId || buyTokenChainId;

  // The route diagram only makes sense for a connected wallet on a same-chain market swap.
  const showSankeyPreview =
    walletsReady &&
    isConnected &&
    checkoutMode === CheckoutModes.market &&
    sellTokenChainId === buyTokenChainId &&
    isTabletOrDesktop;

  return (
    <main
      className="pt-[80px] tablet:pt-[92px]
        [body[data-show-banner='true']_&]:pt-[105px]
        tablet:[body[data-show-banner='true']_&]:pt-[117px]"
    >
      <HomepageHeading hidden={walletConnected} animateEntrance={walletsReady} />

      <div className="flex flex-col gap-6 w-full max-w-[1240px] mx-auto px-4">
        <div className="flex flex-col items-center gap-6 desktop:flex-row desktop:items-start desktop:gap-4">
          <div className="flex flex-col gap-4 w-full max-w-[434px] desktop:w-[434px] shrink-0 desktop:pb-8">
            {autoBannerVisible && bannerChainId && checkoutMode === CheckoutModes.market && (
              <MatchaAutoBanner
                chainId={bannerChainId}
                setAutoBannerVisible={setAutoBannerVisible}
              />
            )}
            <TradeModuleCard
              autoBannerVisible={autoBannerVisible}
              buyToken={buyToken}
              showSankeyPreview={showSankeyPreview}
              setAutoBannerVisible={setAutoBannerVisible}
              onFlipSelectedTokens={handleFlipSelectedTokens}
              onSelectToken={handleSelectToken}
              onSelectPair={handleSelectPair}
              onOpenTokenPanel={handleOpenTokenPanel}
              sellTokenAddress={sellTokenAddress}
              sellTokenChainId={sellTokenChainId}
              buyTokenAddress={buyTokenAddress}
              buyTokenChainId={buyTokenChainId}
            />
          </div>

          <Panel className="contents desktop:flex desktop:flex-col desktop:flex-1 desktop:order-first min-w-0">
            <div className="hidden desktop:block">
              <TokenPanel
                sellToken={sellToken}
                buyToken={buyToken}
                side={side}
                onSideChange={handleSideChange}
                chartHeight={DESKTOP_CHART_HEIGHT}
                active={isDesktopViewport}
                embedded
              />
            </div>
            <HistorySection
              scopeToken={scopeToken}
              scopeReady={scopeReady}
              buySelection={buySelection}
              embedded
              className="w-full min-w-0 bg-special-background-card rounded-2xl desktop:bg-transparent desktop:rounded-t-none desktop:border-t border-token-gray-300"
            />
          </Panel>
        </div>

        <BottomPanelModal
          isOpen={isTokenPanelOpen}
          onClose={() => setTokenPanelOpen(false)}
          onOpenChange={setTokenPanelOpen}
          onOpenAnimationComplete={() => setTokenPanelReady(true)}
          variant="LARGE"
        >
          {/* Phones dismiss the sheet with its drag handle, so the button is
              only exposed to assistive tech there. Tablets get a dialog. */}
          {isTabletOrDesktop ? (
            <div className="flex justify-end px-3 pt-3">
              <Dialog.Close aria-label="close" className={dialogCloseBtnStyles().button()}>
                <XIcon aria-hidden className={dialogCloseBtnStyles().icon()} />
              </Dialog.Close>
            </div>
          ) : (
            <VisuallyHidden>
              <Dialog.Close>Close token details</Dialog.Close>
            </VisuallyHidden>
          )}
          <VisuallyHidden>
            <Dialog.Title>Token details</Dialog.Title>
          </VisuallyHidden>
          <TokenPanel
            sellToken={sellToken}
            buyToken={buyToken}
            side={side}
            onSideChange={handleSideChange}
            active={isTokenPanelOpen && isTokenPanelReady}
            showSwitchButton={false}
            embedded
          />
        </BottomPanelModal>

        {!walletConnected && <MarketingStrip />}
      </div>
    </main>
  );
};
